import { useEffect, useRef } from 'react';
import { useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { BORDERHIGHLIGHTSHADES, COLOURS, HIGHLIGHTSHADES } from '../shared/MapColours';

function GeoJsonLayer({
  geoJson,
  colourKey = null,
  baseStyle = {},
  hoverEnabled = true,
  onMouseOut,
  onMouseOver,
  filter,
  currentSelection,
}) {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const dataLayerRef = useRef(null);
  const hoveredFeatureRef = useRef(null);

  useEffect(() => {
    // Early return if not ready
    if (!isLoaded || !map || !geoJson) return null;

    // Remove previous layer if it exists
    if (dataLayerRef.current) {
      dataLayerRef.current.setMap(null);
    }

    // Create new data layer
    const dataLayer = new window.google.maps.Data({ map });
    dataLayerRef.current = dataLayer;

    // Add GeoJSON
    dataLayer.addGeoJson(geoJson);

    // Set initial style
    dataLayer.setStyle(() => ({
      clickable: true,
      fillColor: colourKey ? COLOURS[colourKey] : 'lightgrey',
      fillOpacity: 1.0,
      strokeColor: '#FFFFFF',
      strokeWeight: 0.5,
      zIndex: 1,
      ...baseStyle,
    }));

    // Hover interactions
    if (hoverEnabled) {
      const mouseOverListener = dataLayer.addListener('mouseover', (event) => {
        hoveredFeatureRef.current = event.feature;
        dataLayer.overrideStyle(event.feature, {
          fillColor: colourKey ? HIGHLIGHTSHADES[colourKey] : 'lightgrey',
          strokeColor: colourKey ? BORDERHIGHLIGHTSHADES[colourKey] : 'grey',
          strokeWeight: 1.5,
          zIndex: 10,
        });
        onMouseOver?.();
      });

      const mouseOutListener = dataLayer.addListener('mouseout', () => {
        if (hoveredFeatureRef.current) {
          dataLayer.revertStyle(hoveredFeatureRef.current);
          hoveredFeatureRef.current = null;
        } else {
          dataLayer.revertStyle();
        }
        onMouseOut?.();
      });

      // Cleanup function
      return () => {
        window.google.maps.event.removeListener(mouseOverListener);
        window.google.maps.event.removeListener(mouseOutListener);
        dataLayer.setMap(null);
      };
    }

    // Cleanup for non-hover case
    return () => {
      dataLayer.setMap(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    colourKey,
    filter,
    currentSelection,
  ]);

  return null;
}

export default GeoJsonLayer;
